/**
 * Redux slice for SMART on FHIR authentication state.
 *
 * SECURITY NOTE:
 * This slice NEVER stores sensitive data like:
 * - Access tokens
 * - Refresh tokens
 * - Client credentials
 * - PKCE verifiers
 *
 * It only stores:
 * - Session token (UUID - safe to store)
 * - Launch context (patient ID, encounter ID, etc.)
 * - Authentication status
 */
import { createSlice } from '@reduxjs/toolkit';
const initialState = {
    sessionToken: null,
    patientId: null,
    patientName: null,
    patientBirthDate: null,
    patientGender: null,
    encounterId: null,
    fhirUserId: null,
    fhirBaseUrl: null,
    tenantId: null,
    providerCode: null,
    scopes: null,
    hasRefreshToken: false,
    isAuthenticated: false,
    isLoading: false,
    error: null,
    // Patient eligibility defaults — fail-open to the normal flow so a session
    // lacking these fields behaves exactly as before.
    aiAvailable: true
};
const smartSlice = createSlice({
    name: 'smart',
    initialState,
    reducers: {
        /**
         * Set loading state
         */
        setLoading: (state, action) => {
            state.isLoading = action.payload;
            if (action.payload) {
                state.error = null;
            }
        },
        /**
         * Set session information after successful authentication
         */
        setSessionInfo: (state, action) => {
            var _a, _b;
            // sessionToken was removed from backend response for security
            // We can try to get it from the URL launch context if needed,
            // but ideally the frontend shouldn't need it if using cookies.
            // However, the slice still has the field.
            // For now, we won't update it if it's missing in payload.
            if (action.payload.sessionToken) {
                state.sessionToken = action.payload.sessionToken;
            }
            state.fhirBaseUrl = action.payload.fhirBaseUrl;
            state.tenantId = (_a = action.payload.tenantId) !== null && _a !== void 0 ? _a : action.payload.fhirBaseUrl;
            state.providerCode = action.payload.providerCode || null;
            state.patientId = action.payload.patient || null;
            state.encounterId = action.payload.encounter || null;
            state.fhirUserId = action.payload.fhirUser || null;
            state.scopes = action.payload.scope || null;
            state.hasRefreshToken = action.payload.hasRefreshToken;
            // Map backend wire fields to UI-named eligibility flags. Default keeps
            // the normal flow when the backend omits the field.
            state.aiAvailable = (_b = action.payload.aiScoringAllowed) !== null && _b !== void 0 ? _b : true;
            state.error = null;
        },
        /**
         * Set authenticated status
         */
        setAuthenticated: (state, action) => {
            state.isAuthenticated = action.payload;
            state.isLoading = false;
        },
        /**
         * Set patient info (fetched separately from Patient resource)
         */
        setPatientInfo: (state, action) => {
            state.patientName = action.payload.name;
            state.patientBirthDate = action.payload.birthDate;
            state.patientGender = action.payload.gender;
        },
        /**
         * Set error state
         */
        setError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
            state.isAuthenticated = false;
        },
        /**
         * Clear error state
         */
        clearError: (state) => {
            state.error = null;
        },
        /**
         * Clear all authentication state (logout)
         */
        clearAuth: () => {
            return Object.assign({}, initialState);
        },
        /**
         * Update session token (after refresh)
         */
        updateSessionToken: (state, action) => {
            state.sessionToken = action.payload;
        }
    }
});
export const { setLoading, setSessionInfo, setAuthenticated, setPatientInfo, setError, clearError, clearAuth, updateSessionToken } = smartSlice.actions;
export default smartSlice.reducer;
